import {Error} from "@/api";
import {ErrorWithContext} from "@dvrt/graphql-widgets";

export type {ErrorWithContext};

type ErrorCollection = Record<string, ErrorWithContext[]>;

export default class ErrorHelper {
    protected errorCollection: ErrorCollection;

    constructor(errors: Error[], locationElevation: number = 0) {
        this.errorCollection = {};
        if (errors.length === 0) return;

        for (const error of errors) {
            this.addError(error.location.slice(locationElevation).join("."), {
                type: error.type,
                context: parseContext(error.context),
            });
        }
    }

    hasError(field: string): boolean {
        return Boolean(this.fieldErrors(field).length);
    }

    /**
     * Returns all errors for a given field.
     *
     * `removePrefix` can be used to remove the "value_error." prefix from the error type.
     * Can be useful if using [<MultipleLineError>] component as it expects only the error code without prefix.
     *
     * Use the `contextComparator` to filter the errors by a given context
     * field and value.
     *
     * For example:
     * ```typescript
     * const contexts = [
     *   {
     *     id: 1,
     *     name: "foo",
     *   },
     *   {
     *     id: 2,
     *     name: "bar",
     *   },
     * ];
     *
     * const filtered = errorHelper.fieldErrors(..., ["name", "foo"]); // return [{id: 1, name: "foo"}]
     * */
    fieldErrors(
        field: string,
        options?: {contextComparator?: (context: Record<string, any>) => boolean; removePrefix?: boolean},
    ): ErrorWithContext[] {
        const {contextComparator, removePrefix = false} = options ?? {};

        let errors = this.errorCollection[field] ?? [];

        if (contextComparator) {
            errors = errors.filter((error) => contextComparator(error.context ?? {}));
        }

        if (removePrefix) {
            errors = errors.map((error) => ({
                ...error,
                type: error.type.replace("value_error.", ""),
            }));
        }

        return errors;
    }

    get allErrors(): ErrorCollection {
        return this.errorCollection;
    }

    protected addError(field: string, error: ErrorWithContext) {
        if (this.errorCollection[field] === undefined) this.errorCollection[field] = [];
        this.errorCollection[field].push(error);
    }
}

export function parseContext(context: string | null | undefined): Record<string, any> {
    if (!context) return {};

    // FIXME: this is a hack to not break anything for now, but this must be fixed -
    // we should somehow either type our errors, or return a simple dict (stringified
    // or not) for the 'context' field.
    try {
        const level1 = JSON.parse(context);
        try {
            const level2 = JSON.parse(level1["context"]);
            return level2;
        } catch {
            return level1;
        }
    } catch {
        return {};
    }
}
